import { createHashRouter, RouterProvider } from "react-router-dom";
import { UsersPage, UserPage } from "features/users";
import {
  LanguagesPage,
  LanguagePage,
  ValidationPage,
  ValidationTemplatesTabPage,
  RulesAssignTab,
  ValidationTemplateTabPage,
} from "features/languages";
import { WorkspacesPage, FilesPage } from "features/workspaces";
import { StatisticsPage, ReportPage } from "features/statistics";
import { ProjectPage } from "features/projects";
import { SyncPage } from "features/dataSync";
import { EditorPage } from "features/editor";
import { Page404 } from "pages/404Page";

import { ErrorBoundary } from "@components";
import Root from "pages/Root";

export const WORKSPACES_PATH = "workspaces";

const getPopulateBreadcrumb = (input: any, populated: any, keys: string[]) => {
  if (!populated) {
    return [];
  }
  const result: { key: string; link: string; label: string }[] = [];
  let link = `/${WORKSPACES_PATH}`;
  let allPopulated = true;

  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    if (populated[key]) {
      link += `/${input[key]}`;

      result.push({
        key,
        link,
        label: populated[key],
      });
    } else {
      allPopulated = false;
      break;
    }
  }

  return allPopulated ? result : [];
};

const router = createHashRouter(
  [
    {
      path: "/",
      element: <Root />,
      children: [
        {
          index: true,
          Component: WorkspacesPage,
        },

        {
          path: WORKSPACES_PATH,
          handle: {
            crumb: () => "Workspaces",
          },
          children: [
            {
              index: true,
              Component: WorkspacesPage,
            },
            {
              path: ":workspaceId",
              element: <WorkspacesPage />,
              handle: {
                crumb: (input: any, populated: any) => getPopulateBreadcrumb(input, populated, ["workspaceId"]),
              },
            },
            {
              path: ":workspaceId/:projectId",
              Component: ProjectPage,
              handle: {
                crumb: (input: any, populated: any) =>
                  getPopulateBreadcrumb(input, populated, ["workspaceId", "projectId"]),
              },
            },
            {
              path: ":workspaceId/:projectId/:languageId",
              Component: FilesPage,
              handle: {
                crumb: (input: any, populated: any) =>
                  getPopulateBreadcrumb(input, populated, ["workspaceId", "projectId", "languageId"]),
              },
            },
            {
              path: ":workspaceId/:workspaceLabel/:projectId/:projectLabel/:languageId/:languageLabel/:fileId/:fileLabel",
              Component: EditorPage,
              handle: {
                crumb: ({
                  workspaceId,
                  workspaceLabel,
                  projectId,
                  projectLabel,
                  languageId,
                  languageLabel,
                  fileId,
                  fileLabel,
                }: {
                  workspaceId: string;
                  workspaceLabel: string;
                  projectId: string;
                  projectLabel: string;
                  languageId: string;
                  languageLabel: string;
                  fileId: string;
                  fileLabel: string;
                }) => [
                  { key: "workspaceId", link: `/${WORKSPACES_PATH}/${workspaceId}`, label: workspaceLabel },
                  { key: "projectId", link: `/${WORKSPACES_PATH}/${workspaceId}/${projectId}`, label: projectLabel },
                  {
                    key: "languageId",
                    link: `/${WORKSPACES_PATH}/${workspaceId}/${projectId}/${languageId}`,
                    label: languageLabel,
                  },
                  {
                    key: "fileId",
                    link: `/${WORKSPACES_PATH}/${workspaceId}/${projectId}/${languageId}/${fileId}`,
                    label: fileLabel,
                  },
                ],
              },
            },
          ],
        },

        {
          path: "statistics",
          handle: {
            crumb: () => "Statistics",
          },

          children: [
            {
              index: true,
              Component: StatisticsPage,
            },
            {
              path: ":id",
              Component: ReportPage,
              handle: {
                crumb: (input: any, populated: any) => getPopulateBreadcrumb(input, populated, ["id"]),
              },
            },
          ],
        },

        {
          path: "users",
          handle: {
            crumb: () => "Users",
          },

          children: [
            {
              index: true,
              Component: UsersPage,
            },
            {
              path: ":id",
              Component: UserPage,
              handle: {
                crumb: (params: { id: string }) => params.id,
              },
            },
            {
              path: ":id/edit",
              element: <UserPage action="edit" />,
              handle: {
                crumb: (params: { id: string }) => params.id,
              },
            },
            {
              path: "create",
              element: <UserPage action="create" />,
              handle: {
                crumb: () => "Create User",
              },
            },
          ],
        },

        {
          path: "languages",
          handle: {
            crumb: () => "Languages",
          },

          children: [
            {
              index: true,
              Component: LanguagesPage,
            },
            {
              path: ":id",
              Component: LanguagePage,
              handle: {
                crumb: (params: { id: string }) => params.id,
              },
            },
            {
              path: ":id/edit",
              element: <LanguagePage action="edit" />,
              handle: {
                crumb: (params: { id: string }) => params.id,
              },
            },
            {
              path: "create",
              element: <LanguagePage action="create" />,
              handle: {
                crumb: () => "Add Language",
              },
            },
          ],
        },

        {
          path: "validation-rules",
          handle: {
            crumb: () => "Validation Rules",
          },
          Component: ValidationPage,
          children: [
            {
              index: true,
              path: "",
              Component: ValidationTemplatesTabPage,
            },
            {
              path: "create",
              element: <ValidationTemplateTabPage action="create" />,
            },
            {
              path: "edit/:id",
              Component: ValidationTemplateTabPage,
            },
            { path: "rules-assign", Component: RulesAssignTab },
          ],
        },

        {
          path: "data-sync",
          handle: {
            crumb: () => "Data synchronization",
          },
          children: [
            {
              index: true,
              Component: SyncPage,
            },
          ],
        },

        {
          path: "*",
          element: <Page404 />,
        },

        {
          path: "404",
          element: <Page404 />,
        },
      ],
      errorElement: <ErrorBoundary />,
    },
  ],
  {
    future: {
      // Normalize `useNavigation()`/`useFetcher()` `formMethod` to uppercase
      v7_normalizeFormMethod: true,
    },
  },
);

export default () => <RouterProvider router={router} />;
